<script setup lang="ts">
import "@maersk-global/mds-components-core-select";
import "@maersk-global/mds-components-core-option";
import { onMounted, computed, ref, watch } from "vue";
import { useScheduleLocationStore } from "@/store/schedule/schedule_location_store";
import { useSearchIdsStore } from "@/store/common/search_id_store";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import type { CountryData, Terminal } from "@/interface/common/location_inteface";
import { useVesselScheduleStore } from "@/store/schedule/vessel_schedule_store";
import { storeToRefs } from "pinia";
import { useGlobalStore } from "@/store/global";
import { appButtonClick } from "@/utils/analytics";

const globalStore = useGlobalStore();
const locationStore = useScheduleLocationStore();
const searchStore = useSearchIdsStore();
const terminalConfigurationStore = useTerminalConfigurationStore();

// Data for regions, countries, and terminals
const regions = computed((): any => locationStore.filteredLocations);

//  Use the location id store to get selected terminal id
const schedulesStore = useVesselScheduleStore();
const { error } = storeToRefs(schedulesStore);
// Fetch the selected terminal id

const getTerminalDetailsById = (regions, terminalId) => {
  for (const region of regions) {
    for (const country of region.countries) {
      for (const terminal of country.terminals) {
        if (terminal.id.toLocaleLowerCase() === terminalId) {
          const selectedAnalyticsTerminalDetails = {
            region: region.name,
            country: country.name,
            terminalName: terminal.name,
            oneStreamCode: terminal.oneStreamCode,
            tabName: "Vessel Schedule"
          };
          locationStore.updateAnayticsTerminalDetails(selectedAnalyticsTerminalDetails);
          return selectedAnalyticsTerminalDetails;
        }
      }
    }
  }
  return null; // Not found
};

const validateIds = async () => {
  const selectedTerminal = getTerminalDetailsById(regions.value, tempLocationValue.value.toLocaleLowerCase());
  await terminalConfigurationStore.fetchConfiguration(tempLocationValue.value.toLocaleLowerCase());
  const terminal = filteredTerminals.value.find((t) => t.oneStreamCode === selectedTerminal.oneStreamCode);
  appButtonClick("Vessel Schedule", "Search", selectedTerminal.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails,
    searchedAssetIds: "N/A"
  });
  searchStore.setTransactionType("VS");
  if (terminal) {
    locationStore.setTerminal(terminal);
  }
  schedulesStore.fetchVesselSchedule();
};

const clearError = (): void => {
  schedulesStore.resetError();
};
const labels = {
  all: globalStore.sitecoreData.common.all,
  region: globalStore.sitecoreData.common.region,
  country: globalStore.sitecoreData.common.country,
  location: globalStore.sitecoreData.common.location,
  selectlocation: globalStore.sitecoreData.placeHolders.selectLocation,
  search: globalStore.sitecoreData.common.search
};

const allOption = labels.all;
const selectedRegion = ref<string | null>(allOption);
const selectedCountry = ref<string | null>(allOption);
const selectedTerminal = ref<string | null>(null);
const isManualChange = ref(false); // To track if the change is manual
const tempLocationValue = ref<string>("");

onMounted(async () => {
  await locationStore.fetchScheduleLocations();
  const terminalFromUrl = new URLSearchParams(window.location.search).get("terminal") || "";
  const journeyFromUrl = new URLSearchParams(window.location.search).get("journey");

  if (terminalFromUrl !== "") {
    selectedTerminal.value = terminalFromUrl.toUpperCase();
    updateSelectionFromTerminal();
    updateTerminal();
    if (journeyFromUrl === "schedule" || window.location.href.includes("vessel-schedule")) {
      validateIds();
    }
  } else {
    selectedTerminal.value = locationStore.terminalSelected.id;
  }
});

// Function to update region and country based on selected terminal
const updateSelectionFromTerminal = (): void => {
  if (selectedTerminal.value) {
    for (const region of regions.value) {
      for (const country of region.countries) {
        const terminal = country.terminals.find((t) => t.id === selectedTerminal.value);
        if (terminal) {
          isManualChange.value = false; // Not a manual change
          selectedRegion.value = region.name;
          selectedCountry.value = country.name;
          return;
        }
      }
    }
  }
};

// Computed property to get available countries based on selected region
const availableCountries = computed((): CountryData[] => {
  if (selectedRegion.value === allOption) {
    return regions.value.flatMap((region): CountryData[] => region.countries);
  }
  const region = regions.value.find((r): boolean => r.name === selectedRegion.value);
  return region ? region.countries : [];
});

// Computed property to get available terminals based on selected country
const filteredTerminals = computed((): Terminal[] => {
  if (selectedRegion.value === allOption && selectedCountry.value !== allOption) {
    return regions.value.flatMap(
      (region): Terminal[] =>
        region.countries.find((country): boolean => country.name === selectedCountry.value)?.terminals || []
    );
  }
  if (selectedRegion.value === allOption && selectedCountry.value === allOption) {
    return regions.value.flatMap((region): Terminal[] =>
      region.countries.flatMap((country): Terminal[] => country.terminals)
    );
  }
  if (selectedRegion.value !== allOption && selectedCountry.value === allOption) {
    const region = regions.value.find((r): boolean => r.name === selectedRegion.value);
    return region ? region.countries.flatMap((country): Terminal[] => country.terminals) : [];
  }
  if (selectedRegion.value !== allOption && selectedCountry.value !== allOption) {
    const region = regions.value.find((r): boolean => r.name === selectedRegion.value);
    return region
      ? region.countries.find((country): boolean => country.name === selectedCountry.value)?.terminals || []
      : [];
  }
  return regions.value.flatMap((region): Terminal[] =>
    region.countries.flatMap((country): Terminal[] => country.terminals)
  );
});

// Function to handle terminal selection
const updateTerminal = (): void => {
  if (selectedTerminal.value === "") {
    locationStore.setTerminal({} as Terminal);
  } else {
    tempLocationValue.value = selectedTerminal.value;
  }
};

// Watch terminal selection to update region and country
watch(selectedTerminal, (newTerminal) => {
  if (newTerminal) {
    updateSelectionFromTerminal();
  }
});

// Handle manual changes in region and country
const handleManualChange = (): void => {
  isManualChange.value = true;
};

// Watch for manual changes in selected region
watch(selectedRegion, () => {
  if (isManualChange.value) {
    selectedCountry.value = allOption; // Reset country
    selectedTerminal.value = null;
    tempLocationValue.value = "";
  }
});

// Watch for manual changes in selected country
watch(selectedCountry, () => {
  if (isManualChange.value) {
    selectedTerminal.value = null; // Reset terminal
    tempLocationValue.value = "";
  }
});
</script>

<template>
  <div class="country-grid-listing">
    <!-- Region Selection -->
    <div class="grid-item">
      <mc-select v-model="selectedRegion" :label="labels.region" @optionselected="handleManualChange">
        <mc-option :value="allOption">{{ labels.all }}</mc-option>
        <mc-option v-for="region in regions" :key="region.name" :value="region.name">{{ region.name }}</mc-option>
      </mc-select>
    </div>

    <!-- Country Selection -->
    <div class="grid-item">
      <mc-select v-model="selectedCountry" :label="labels.country" @optionselected="handleManualChange">
        <mc-option :value="allOption">{{ labels.all }}</mc-option>
        <mc-option v-for="country in availableCountries" :key="country.name" :value="country.name">
          {{ country.name }}
        </mc-option>
      </mc-select>
    </div>

    <!-- Terminal Selection -->
    <div class="grid-item">
      <mc-select
        v-model="selectedTerminal"
        label="Location*"
        required
        :placeholder="labels.selectlocation"
        @optionselected="updateTerminal"
      >
        <mc-option v-for="terminal in filteredTerminals" :key="terminal.id" :value="terminal.id">
          {{ terminal.name }}
        </mc-option>
      </mc-select>
    </div>
  </div>
  <div v-if="error">
    <div class="divider"></div>
    <mc-notification
      appearance="error"
      fit="small"
      icon="exclamation-octagon"
      closable
      data-test="error-message-container"
      @close="clearError()"
    >
      <span>
        {{ globalStore.sitecoreData.http_errors[error] }}
      </span>
    </mc-notification>
  </div>

  <section class="search-actions item-inline action-buttons button-top-margin-15px">
    <mc-button
      :disabled="tempLocationValue === ''"
      data-test="search-button"
      fit="medium"
      :label="labels.search"
      :loading="schedulesStore.isLoading"
      @click="validateIds"
    >
    </mc-button>
  </section>
</template>
